// PATH: src/App.jsx
import React, { Suspense, lazy, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import AppLayout from "sites/construction/components/layout/AppLayout/AppLayout";
import RealStateLayout from "sites/realstate/components/layout/AppLayout/AppLayout";

import { useTheme } from "app/providers/ThemeProvider";
import "./App.css";

const PortalHomePage = lazy(() => import("sites/group/pages/PortalHomePage"));
const TradingPage = lazy(() => import("sites/group/divisions/TradingPage"));

const RealEstateIntroSection = lazy(() =>
  import("sites/realstate/features/home/sections/RealEstateIntro/RealEstateIntroSection")
);

const FeaturedProperties = lazy(() =>
  import("sites/realstate/features/home/sections/FeaturedProperties/FeaturedProperties")
);

const RealEstateShares = lazy(() => import("sites/realstate/features/home/sections/RealEstateShares/RealEstate"));

const CompraPage = lazy(() => import("sites/realstate/pages/compra/CompraPage"));
const VentaPage = lazy(() => import("sites/realstate/pages/venta/VentaPage"));
const RentaPage = lazy(() => import("sites/realstate/pages/renta/RentaPage"));
const AboutRealEstatePage = lazy(() => import("sites/realstate/pages/About/AboutRealEstate"));
const ContactoRealEstatePage = lazy(() => import("sites/realstate/pages/contacto/ContactoPage"));

const ConstructionHomePage = lazy(() => import("sites/construction/pages/construction/ConstructionHomePage"));
const AboutPage = lazy(() => import("sites/construction/pages/about/AboutPage"));
const ContactPage = lazy(() => import("sites/construction/pages/contact/ContactPage"));

const ServicesPage = lazy(() => import("sites/construction/pages/services/ServicesPage"));
const EdificacionPage = lazy(() => import("sites/construction/pages/services/EdificacionPage"));
const InfrastructurePage = lazy(() => import("sites/construction/pages/services/InfrastructurePage"));
const SpecialtiesPage = lazy(() => import("sites/construction/pages/services/SpecialtiesPage"));
const EngineeringPage = lazy(() => import("sites/construction/pages/services/EngineeringPage"));

function LoadingFallback() {
  return (
    <div className="App__loading" role="status" aria-live="polite" aria-label="Cargando">
      <div className="App__spinner" />
    </div>
  );
}

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch { }
}

function getScrollHost() {
  if (typeof document === "undefined") {
    return { container: null, host: typeof window !== "undefined" ? window : null };
  }
  const container = document.querySelector(".App");
  return { container, host: container || window };
}

function getNavHeight() {
  if (typeof window === "undefined") return 76;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue("--navbar-height");
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 76;
}

function scrollToTop(behavior = "smooth") {
  const { container } = getScrollHost();
  if (container) container.scrollTo({ top: 0, behavior });
  else if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior });
}

function scrollToId(id) {
  if (typeof document === "undefined") return false;
  const el = document.getElementById(id);
  if (!el) return false;

  const navH = getNavHeight();
  const { container } = getScrollHost();

  if (container) {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - containerRect.top + container.scrollTop - navH;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return true;
  }

  const top = el.getBoundingClientRect().top + window.scrollY - navH;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  return true;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isSectionRoute = pathname === "/constructora/contacto/seccion" || pathname === "/constructora/alianzas/seccion";
    if (isSectionRoute) return;
    scrollToTop("auto");
  }, [pathname]);

  return null;
}

function RememberDivisionOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const set = (v) => safeSessionSet("arca:lastDivision", v);

    if (pathname === "/constructora" || pathname.startsWith("/constructora/")) set("constructora");
    else if (pathname === "/comercializadora" || pathname.startsWith("/comercializadora/")) set("comercializadora");
    else if (pathname === "/inmobiliaria" || pathname.startsWith("/inmobiliaria/")) set("inmobiliaria");
    else if (pathname === "/portal") set("portal");
  }, [pathname]);

  return null;
}

function RootRoute() {
  if (typeof window !== "undefined") {
    const last = safeSessionGet("arca:lastDivision");
    if (last === "constructora") return <Navigate to="/constructora" replace />;
    if (last === "comercializadora") return <Navigate to="/comercializadora" replace />;
    if (last === "inmobiliaria") return <Navigate to="/inmobiliaria" replace />;
    if (last === "portal") return <Navigate to="/portal" replace />;
  }
  return <PortalHomePage />;
}

function NosotrosAliasRoute() {
  if (typeof window !== "undefined") {
    const last = safeSessionGet("arca:lastDivision");
    if (last === "constructora") return <Navigate to="/constructora/nosotros" replace />;
    if (last === "inmobiliaria") return <Navigate to="/inmobiliaria/nosotros" replace />;
    if (last === "comercializadora") return <Navigate to="/comercializadora" replace />;
    if (last === "portal") return <Navigate to="/portal" replace />;
  }
  return <Navigate to="/constructora/nosotros" replace />;
}

function ContactoAliasRoute() {
  if (typeof window !== "undefined") {
    const last = safeSessionGet("arca:lastDivision");
    if (last === "constructora") return <Navigate to="/constructora/contacto" replace />;
    if (last === "inmobiliaria") return <Navigate to="/inmobiliaria/contacto" replace />;
    if (last === "comercializadora") return <Navigate to="/comercializadora" replace />;
    if (last === "portal") return <Navigate to="/portal" replace />;
  }
  return <Navigate to="/constructora/contacto" replace />;
}

function ContactSectionRoute() {
  useEffect(() => {
    let tries = 0;
    let timerId = null;

    const tick = () => {
      tries += 1;
      const ok = scrollToId("contact");
      if (!ok && tries < 30) timerId = window.setTimeout(tick, 80);
    };

    timerId = window.setTimeout(tick, 0);
    return () => {
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  return <ConstructionHomePage />;
}

function AlliancesSectionRoute() {
  useEffect(() => {
    let tries = 0;
    let timerId = null;

    const tick = () => {
      tries += 1;
      const ok = scrollToId("works");
      if (!ok && tries < 30) timerId = window.setTimeout(tick, 80);
    };

    timerId = window.setTimeout(tick, 0);
    return () => {
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  return <ConstructionHomePage />;
}

function ProjectsRoute() {
  useEffect(() => {
    scrollToTop("smooth");
  }, []);

  return <ConstructionHomePage />;
}

function RealEstateHomeFeaturePage() {
  return (
    <main>
      <RealEstateIntroSection />
      <FeaturedProperties />
      <RealEstateShares />
    </main>
  );
}

function App() {
  const { darkMode } = useTheme();

  const appStyle = useMemo(
    () => ({
      background: darkMode ? "#000" : "#fff",
      color: darkMode ? "#fff" : "#000",
    }),
    [darkMode]
  );

  return (
    <Router>
      <div className="App" style={appStyle}>
        <ScrollToTopOnRouteChange />
        <RememberDivisionOnRouteChange />

        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<RootRoute />} />

            <Route path="/portal" element={<PortalHomePage />} />
            <Route path="/comercializadora" element={<TradingPage />} />

            <Route path="/inmobiliaria" element={<RealStateLayout />}>
              <Route index element={<RealEstateHomeFeaturePage />} />
              <Route path="inicio" element={<Navigate to="/inmobiliaria" replace />} />
              <Route path="compra" element={<CompraPage />} />
              <Route path="venta" element={<VentaPage />} />
              <Route path="renta" element={<RentaPage />} />
              <Route path="nosotros" element={<AboutRealEstatePage />} />
              <Route path="contacto" element={<ContactoRealEstatePage />} />
              <Route path="*" element={<Navigate to="/inmobiliaria" replace />} />
            </Route>

            <Route path="/constructora" element={<AppLayout />}>
              <Route index element={<ConstructionHomePage />} />
              <Route path="nosotros" element={<AboutPage />} />
              <Route path="contacto" element={<ContactPage />} />
              <Route path="contacto/seccion" element={<ContactSectionRoute />} />
              <Route path="alianzas" element={<Navigate to="alianzas/seccion" replace />} />
              <Route path="alianzas/seccion" element={<AlliancesSectionRoute />} />
              <Route path="proyectos" element={<ProjectsRoute />} />
              <Route path="servicios" element={<ServicesPage />} />
              <Route path="servicios/edificacion" element={<EdificacionPage />} />
              <Route path="servicios/infraestructura" element={<InfrastructurePage />} />
              <Route path="servicios/especialidades" element={<SpecialtiesPage />} />
              <Route path="servicios/ingenieria" element={<EngineeringPage />} />
              <Route path="*" element={<Navigate to="/constructora" replace />} />
            </Route>

            <Route path="/compra" element={<Navigate to="/inmobiliaria/compra" replace />} />
            <Route path="/venta" element={<Navigate to="/inmobiliaria/venta" replace />} />
            <Route path="/renta" element={<Navigate to="/inmobiliaria/renta" replace />} />

            <Route path="/construction" element={<Navigate to="/constructora" replace />} />
            <Route path="/trading" element={<Navigate to="/comercializadora" replace />} />
            <Route path="/realstate" element={<Navigate to="/inmobiliaria" replace />} />

            <Route path="/nosotros" element={<NosotrosAliasRoute />} />
            <Route path="/nosotros-constructora" element={<Navigate to="/constructora/nosotros" replace />} />
            <Route path="/nosotros-inmobiliaria" element={<Navigate to="/inmobiliaria/nosotros" replace />} />
            <Route path="/nosotros-comercializadora" element={<Navigate to="/comercializadora" replace />} />
            <Route path="/about" element={<Navigate to="/constructora/nosotros" replace />} />
            <Route path="/acerca-de" element={<Navigate to="/constructora/nosotros" replace />} />
            <Route path="/quienes-somos" element={<Navigate to="/constructora/nosotros" replace />} />

            <Route path="/contacto" element={<ContactoAliasRoute />} />
            <Route path="/contacto-constructora" element={<Navigate to="/constructora/contacto" replace />} />
            <Route path="/contacto-inmobiliaria" element={<Navigate to="/inmobiliaria/contacto" replace />} />
            <Route path="/contacto-comercializadora" element={<Navigate to="/comercializadora" replace />} />
            <Route path="/contacto/seccion" element={<Navigate to="/constructora/contacto/seccion" replace />} />

            <Route path="/alianzas" element={<Navigate to="/constructora/alianzas/seccion" replace />} />
            <Route path="/alianzas-constructora" element={<Navigate to="/constructora/alianzas/seccion" replace />} />
            <Route path="/alianzas/seccion" element={<Navigate to="/constructora/alianzas/seccion" replace />} />

            <Route path="/projects" element={<Navigate to="/constructora/proyectos" replace />} />
            <Route path="/proyectos" element={<Navigate to="/constructora/proyectos" replace />} />
            <Route path="/proyectos-constructora" element={<Navigate to="/constructora/proyectos" replace />} />

            <Route path="/servicios" element={<Navigate to="/constructora/servicios" replace />} />
            <Route path="/servicios-constructora" element={<Navigate to="/constructora/servicios" replace />} />
            <Route path="/servicios/edificacion" element={<Navigate to="/constructora/servicios/edificacion" replace />} />
            <Route path="/servicios/infraestructura" element={<Navigate to="/constructora/servicios/infraestructura" replace />} />
            <Route
              path="/servicios/especialidades"
              element={<Navigate to="/constructora/servicios/especialidades" replace />}
            />
            <Route path="/servicios/ingenieria" element={<Navigate to="/constructora/servicios/ingenieria" replace />} />

            <Route path="/Services" element={<Navigate to="/constructora/servicios" replace />} />
            <Route path="/Services/ServicePage" element={<Navigate to="/constructora/servicios" replace />} />
            <Route
              path="/Services/EdificationPage"
              element={<Navigate to="/constructora/servicios/edificacion" replace />}
            />
            <Route
              path="/Services/InfrastructurePage"
              element={<Navigate to="/constructora/servicios/infraestructura" replace />}
            />
            <Route
              path="/Services/SpecialtiesPage"
              element={<Navigate to="/constructora/servicios/especialidades" replace />}
            />
            <Route
              path="/Services/EngineeringPage"
              element={<Navigate to="/constructora/servicios/ingenieria" replace />}
            />

            <Route path="/services" element={<Navigate to="/constructora/servicios" replace />} />
            <Route path="/services/*" element={<Navigate to="/constructora/servicios" replace />} />
            <Route path="/ServicesPage/ServicePage" element={<Navigate to="/constructora/servicios" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
